import {
  diffStates,
  type Message,
  type ParsedTurn,
  type StateDiffEntry,
  type TurnDiagnostic,
} from "@roqeet/core";

export const MODULE_STATE_BEFORE = "moduleStateBefore";
export const MODULE_STATE_AFTER = "moduleStateAfter";
export const MODULE_TURN_DEBUG = "moduleTurnDebug";
export const MODULE_TURN_PARSED = "moduleTurnParsed";
export const MODULE_TURN_DIFF = "moduleTurnDiff";
export const MODULE_TURN_DIAGNOSTICS = "moduleTurnDiagnostics";

export interface ModuleTurnDebug {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  diff: StateDiffEntry[];
  parsed: ParsedTurn;
  diagnostics: TurnDiagnostic[];
  ended: boolean;
  endingLabel?: string;
}

export function buildModuleTurnDebug(input: {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  parsed: ParsedTurn;
  diagnostics?: TurnDiagnostic[];
  ended?: boolean;
  endingLabel?: string;
}): ModuleTurnDebug {
  const diagnostics = [...input.parsed.diagnostics, ...(input.diagnostics ?? [])];
  const debug: ModuleTurnDebug = {
    before: cloneRecord(input.before),
    after: cloneRecord(input.after),
    diff: diffStates(input.before, input.after),
    parsed: cloneParsedTurn(input.parsed),
    diagnostics,
    ended: input.ended ?? false,
  };
  if (input.endingLabel) debug.endingLabel = input.endingLabel;
  return debug;
}

export function getModuleTurnDebug(message: Message): ModuleTurnDebug | undefined {
  const variant = message.variants[message.activeVariant];
  const variantDebug = asDebug(variant?.extensions?.[MODULE_TURN_DEBUG]);
  if (variantDebug) return variantDebug;

  const ext = message.extensions ?? {};
  const before = asRecord(ext[MODULE_STATE_BEFORE]);
  const after = asRecord(ext[MODULE_STATE_AFTER]);
  const parsed = asParsedTurn(ext[MODULE_TURN_PARSED]);
  if (!before || !after || !parsed) return undefined;

  const diagnostics = asDiagnostics(ext[MODULE_TURN_DIAGNOSTICS]) ?? parsed.diagnostics;
  return {
    before,
    after,
    diff: asDiff(ext[MODULE_TURN_DIFF]) ?? diffStates(before, after),
    parsed,
    diagnostics,
    ended: after["ended"] === true,
    ...(typeof after["endingLabel"] === "string" ? { endingLabel: after["endingLabel"] } : {}),
  };
}

function asDebug(value: unknown): ModuleTurnDebug | undefined {
  if (!isRecord(value)) return undefined;
  const before = asRecord(value["before"]);
  const after = asRecord(value["after"]);
  const parsed = asParsedTurn(value["parsed"]);
  if (!before || !after || !parsed) return undefined;
  return {
    before,
    after,
    parsed,
    diff: asDiff(value["diff"]) ?? diffStates(before, after),
    diagnostics: asDiagnostics(value["diagnostics"]) ?? parsed.diagnostics,
    ended: value["ended"] === true,
    ...(typeof value["endingLabel"] === "string" ? { endingLabel: value["endingLabel"] } : {}),
  };
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? value : undefined;
}

function asParsedTurn(value: unknown): ParsedTurn | undefined {
  if (!isRecord(value)) return undefined;
  if (typeof value["narration"] !== "string") return undefined;
  if (!Array.isArray(value["choices"])) return undefined;
  if (!Array.isArray(value["stateDelta"])) return undefined;
  if (!Array.isArray(value["rolls"])) return undefined;
  if (!Array.isArray(value["diagnostics"])) return undefined;
  if (typeof value["raw"] !== "string") return undefined;
  return value as unknown as ParsedTurn;
}

function asDiff(value: unknown): StateDiffEntry[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is StateDiffEntry => {
    if (!isRecord(item)) return false;
    return (
      typeof item["key"] === "string" &&
      (item["change"] === "added" || item["change"] === "removed" || item["change"] === "changed")
    );
  });
}

function asDiagnostics(value: unknown): TurnDiagnostic[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is TurnDiagnostic => {
    if (!isRecord(item)) return false;
    return item["severity"] === "warning" && typeof item["message"] === "string";
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function cloneRecord(value: Record<string, unknown>): Record<string, unknown> {
  return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
}

function cloneParsedTurn(value: ParsedTurn): ParsedTurn {
  return JSON.parse(JSON.stringify(value)) as ParsedTurn;
}
